// Bottom-of-sidebar collection dock (desktop). A vertical stack pinned at the
// bottom-left (over the tool sidebar): an optional header (the VR button) over
// grid-icon openers — Layouts (floor mode only) over Models. Each opener toggles
// a shared thumbnail DRAWER that expands as a strip to the right of the sidebar
// (opening one closes the other). Model tiles: click LOADS (model mode) or PLACES
// (layout mode, also draggable onto the canvas). Layout tiles: click LOADS the
// plan. Both have hover Export/Delete. Data lives in the collection-store /
// floor-plan-store; this owns the DOM.

import { deleteModel, downloadModel, listModels, putModel } from "../model/collection-store";
import { deletePlan, downloadPlan, listPlans, putPlan } from "../floor/floor-plan-store";
import type { SavedModel } from "../model/saved-model";
import type { SavedFloorPlan } from "../floor/saved-floor-plan";
import { showConfirm, showPrompt } from "./modal";
import { GIZMO_SIZE } from "./gizmo";

export interface CollectionDock {
  refresh(): void;
  setMode(mode: "modeling" | "floorplan"): void;
  setVisible(v: boolean): void;
}

const SIDEBAR_W = 192;
const CSS = `
/* Bottom-left stack (VR button + collection openers), over the tool sidebar.
   An opaque footer spanning the full sidebar width so scrolled-up tools never
   peek through the gaps around these buttons; padding keeps the buttons in the
   same place as the sidebar content (12px inset). Colors match the sidebar
   gradient so it reads as a seamless footer. */
#dt-dock { position: fixed; left: 0; bottom: 0; width: ${SIDEBAR_W}px; box-sizing: border-box;
  z-index: 11; display: none; flex-direction: column; gap: 8px;
  padding: 10px 12px 12px;
  background: linear-gradient(90deg, #1a1b26, #14151e);
  border-right: 1px solid rgba(255,255,255,0.08);
  font: 12px/1.3 system-ui, -apple-system, sans-serif; color: #e7e9f0; }
#dt-dock.show { display: flex; }
#dt-dock .opener {
  height: 34px; border-radius: 8px; cursor: pointer; padding: 0 10px; gap: 8px;
  box-sizing: border-box; display: flex; flex-direction: row; align-items: center;
  border: 1px solid rgba(255,255,255,0.16); background: rgba(28,30,38,0.92); color: #cdd2de;
}
#dt-dock .opener:hover { background: #34384a; color: #fff; }
#dt-dock .opener.active { border-color: #6fa0ff; color: #9fc0ff; }
#dt-dock .opener .ico { flex: none; display: flex; }
#dt-dock .opener .ico svg { width: 18px; height: 18px; display: block; }
#dt-dock .opener .lbl { flex: none; font-weight: 600; }
#dt-dock .opener .chev { flex: none; display: flex; margin-left: auto; }
#dt-dock .opener .chev svg { width: 14px; height: 14px; display: block; transition: transform 0.16s ease; }
#dt-dock .opener.active .chev svg { transform: rotate(180deg); }

/* Thumbnail drawer — a strip to the right of the sidebar, shown while open.
   Stops short of the navigation gizmo's bottom-right viewport. */
#dt-drawer { position: fixed; left: ${SIDEBAR_W + 12}px; right: ${GIZMO_SIZE + 12}px; bottom: 12px;
  height: 118px; z-index: 11; display: none; gap: 10px; align-items: center;
  box-sizing: border-box; padding: 8px 10px; overflow-x: auto; overflow-y: hidden;
  border-radius: 10px; border: 1px solid rgba(255,255,255,0.12);
  background: rgba(28,30,38,0.92); backdrop-filter: blur(14px); -webkit-backdrop-filter: blur(14px);
  font: 12px/1.3 system-ui, -apple-system, sans-serif; color: #e7e9f0; }
#dt-drawer.show { display: flex; }
#dt-drawer .tile {
  position: relative; flex: none; width: 96px; height: 96px; border-radius: 8px;
  box-sizing: border-box; overflow: hidden; background: #14151f;
  border: 1px solid rgba(255,255,255,0.1); cursor: pointer;
}
#dt-drawer .tile img { width: 100%; height: 100%; object-fit: cover; display: block; pointer-events: none; }
#dt-drawer .tile .ph {
  position: absolute; inset: 0; display: flex; align-items: center; justify-content: center;
  color: rgba(231,233,240,0.35);
}
#dt-drawer .tile .ph svg { width: 34px; height: 34px; }
#dt-drawer .tile .nm {
  position: absolute; left: 0; right: 0; bottom: 0; padding: 2px 4px; font-size: 11px;
  background: rgba(10,11,16,0.7); white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
#dt-drawer .tile .mini {
  position: absolute; top: 3px; width: 20px; height: 20px; padding: 0; line-height: 18px;
  border-radius: 5px; border: none; cursor: pointer; font-size: 13px; display: none;
  background: rgba(10,11,16,0.75); color: #e7e9f0;
}
#dt-drawer .tile:hover .mini { display: block; }
#dt-drawer .tile .del { right: 3px; }
#dt-drawer .tile .exp { right: 27px; }
#dt-drawer .tile .ren { right: 51px; }
#dt-drawer .empty-msg { color: rgba(231,233,240,0.5); padding: 0 6px; align-self: center; }
`;

const SVG = (inner: string): string =>
  `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" ` +
  `stroke-linecap="round" stroke-linejoin="round">${inner}</svg>`;
const ICON_GRID = SVG(
  '<rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/>' +
    '<rect x="14" y="14" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/>',
);
const ICON_LAYOUT = SVG(
  '<rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/>' +
    '<line x1="9" y1="21" x2="9" y2="9"/>',
);
const ICON_CHEVRON = SVG('<polyline points="9 6 15 12 9 18"/>'); // right; flips when open

export function installCollectionDock(opts: {
  onLoadModel: (m: SavedModel) => void; // Model mode: load to edit
  onPlaceModelCenter: (m: SavedModel) => void; // Layout: click places at view center
  onPlaceModelAt: (m: SavedModel, clientX: number, clientY: number) => void; // Layout: drop
  onLoadLayout: (p: SavedFloorPlan) => void; // Layout: load the plan
  canvas: HTMLElement;
  headerEl?: HTMLElement; // placed atop the stack (the VR button) — pulled in-flow
}): CollectionDock {
  const style = document.createElement("style");
  style.textContent = CSS;
  document.head.appendChild(style);

  const opener = (icon: string, label: string, title: string): HTMLButtonElement => {
    const b = document.createElement("button");
    b.className = "opener";
    b.title = title;
    b.innerHTML =
      `<span class="ico">${icon}</span><span class="lbl">${label}</span>` +
      `<span class="chev">${ICON_CHEVRON}</span>`;
    return b;
  };

  const root = document.createElement("div");
  root.id = "dt-dock";
  if (opts.headerEl) {
    // The VR button is normally position:fixed; pull it into the flex stack.
    const h = opts.headerEl;
    h.style.position = "static";
    h.style.left = "auto";
    h.style.bottom = "auto";
    h.style.width = "100%";
    h.style.margin = "0";
    root.appendChild(h);
  }
  const layoutsOpener = opener(ICON_LAYOUT, "Layouts", "Saved layouts");
  const modelsOpener = opener(ICON_GRID, "Models", "Model collection");
  root.append(layoutsOpener, modelsOpener);
  document.body.appendChild(root);

  const drawer = document.createElement("div");
  drawer.id = "dt-drawer";
  document.body.appendChild(drawer);

  // Keep each tool sidebar's scroll area ending at the top of the dock: its
  // bottom inset tracks the live dock height (taller in floor mode with the
  // Layouts opener, shorter in model mode, 0 when hidden). Without this the
  // sidebar scrolls behind the opaque footer and the last tools are unreachable.
  // Both mode sidebars share the dock: #dt-ui (model mode), #fp-ui (floor mode).
  const syncSidebarInset = (): void => {
    const h = root.classList.contains("show") ? root.offsetHeight : 0;
    for (const id of ["dt-ui", "fp-ui"]) {
      const sidebar = document.getElementById(id);
      if (sidebar) sidebar.style.bottom = h ? `${h}px` : "";
    }
  };
  if (typeof ResizeObserver !== "undefined") {
    new ResizeObserver(syncSidebarInset).observe(root);
  }

  let mode: "modeling" | "floorplan" = "modeling";
  let models: SavedModel[] = [];
  let plans: SavedFloorPlan[] = [];
  let dragging: SavedModel | null = null;
  let open: "models" | "layouts" | null = null;

  function setOpen(which: "models" | "layouts" | null): void {
    open = which;
    drawer.classList.toggle("show", open !== null);
    layoutsOpener.classList.toggle("active", open === "layouts");
    modelsOpener.classList.toggle("active", open === "models");
    rebuildDrawer();
  }
  layoutsOpener.addEventListener("click", () => setOpen(open === "layouts" ? null : "layouts"));
  modelsOpener.addEventListener("click", () => setOpen(open === "models" ? null : "models"));

  // Drag-to-place: the canvas accepts a dragged model thumbnail (Layout mode).
  opts.canvas.addEventListener("dragover", (e) => {
    if (dragging) e.preventDefault();
  });
  opts.canvas.addEventListener("drop", (e) => {
    if (!dragging) return;
    e.preventDefault();
    const m = dragging;
    dragging = null;
    opts.onPlaceModelAt(m, e.clientX, e.clientY);
    // Leave the drawer open so several models can be dragged in a row.
  });

  function mini(cls: string, text: string, title: string): HTMLButtonElement {
    const b = document.createElement("button");
    b.className = `mini ${cls}`;
    b.textContent = text;
    b.title = title;
    return b;
  }
  // Shared tile: thumbnail (or placeholder), name, hover Export/Delete, click.
  function makeTile(spec: {
    name: string;
    thumbnail?: string;
    draggable: boolean;
    onClick: () => void;
    onDragStart?: () => void;
    onRename: () => void;
    onExport: () => void;
    onDelete: () => void;
  }): HTMLElement {
    const t = document.createElement("div");
    t.className = "tile";
    t.title = spec.name;
    if (spec.thumbnail) {
      const img = document.createElement("img");
      img.src = spec.thumbnail;
      img.alt = "";
      t.appendChild(img);
    } else {
      const ph = document.createElement("div");
      ph.className = "ph";
      ph.innerHTML = ICON_LAYOUT;
      t.appendChild(ph);
    }
    const nm = document.createElement("div");
    nm.className = "nm";
    nm.textContent = spec.name;
    t.appendChild(nm);

    const ren = mini("ren", "✎", "Rename");
    ren.addEventListener("click", (e) => {
      e.stopPropagation();
      spec.onRename();
    });
    const exp = mini("exp", "⤓", "Export");
    exp.addEventListener("click", (e) => {
      e.stopPropagation();
      spec.onExport();
    });
    const del = mini("del", "✕", "Delete");
    del.addEventListener("click", (e) => {
      e.stopPropagation();
      spec.onDelete();
    });
    t.append(ren, exp, del);

    t.draggable = spec.draggable;
    t.addEventListener("dragstart", () => spec.onDragStart?.());
    t.addEventListener("dragend", () => {
      dragging = null;
    });
    t.addEventListener("click", () => spec.onClick());
    return t;
  }

  // Prompt for a new name and persist it. `kind` is just the dialog wording;
  // `persist` writes the renamed record back to its store. The list order
  // (by updatedAt) is left untouched so the tile stays put after renaming.
  async function renameEntry(
    kind: "model" | "layout",
    current: string,
    persist: (name: string) => Promise<unknown>,
  ): Promise<void> {
    const next = await showPrompt(`Rename this ${kind}:`, {
      title: `Rename ${kind}`,
      confirmLabel: "Rename",
      initial: current,
      placeholder: current,
    });
    if (next === null) return; // cancelled
    const name = next.trim();
    if (!name || name === current) return;
    await persist(name);
    refresh();
  }

  function emptyMsg(text: string): void {
    const e = document.createElement("div");
    e.className = "empty-msg";
    e.textContent = text;
    drawer.appendChild(e);
  }

  function rebuildDrawer(): void {
    drawer.replaceChildren();
    if (open === "models") {
      if (models.length === 0) {
        emptyMsg("No saved models yet.");
        return;
      }
      for (const m of models) {
        drawer.appendChild(
          makeTile({
            name: m.name,
            thumbnail: m.thumbnail,
            draggable: mode === "floorplan",
            onClick: () => (mode === "floorplan" ? opts.onPlaceModelCenter(m) : opts.onLoadModel(m)),
            onDragStart: () => {
              dragging = m;
            },
            onRename: () =>
              renameEntry("model", m.name, (name) => putModel({ ...m, name })),
            onExport: () => downloadModel(m),
            onDelete: async () => {
              if (
                await showConfirm(`Delete "${m.name}"? This can't be undone.`, {
                  title: "Delete model",
                  confirmLabel: "Delete",
                  danger: true,
                })
              ) {
                await deleteModel(m.id);
                refresh();
              }
            },
          }),
        );
      }
    } else if (open === "layouts") {
      if (plans.length === 0) {
        emptyMsg("No saved layouts yet.");
        return;
      }
      for (const p of plans) {
        drawer.appendChild(
          makeTile({
            name: p.name,
            thumbnail: p.thumbnail,
            draggable: false,
            onClick: () => {
              opts.onLoadLayout(p);
              setOpen(null);
            },
            onRename: () =>
              renameEntry("layout", p.name, (name) => putPlan({ ...p, name })),
            onExport: () => void downloadPlan(p),
            onDelete: async () => {
              if (
                await showConfirm(`Delete layout "${p.name}"? This can't be undone.`, {
                  title: "Delete layout",
                  confirmLabel: "Delete",
                  danger: true,
                })
              ) {
                await deletePlan(p.id);
                refresh();
              }
            },
          }),
        );
      }
    }
  }

  async function refresh(): Promise<void> {
    try {
      models = await listModels();
    } catch {
      models = [];
    }
    try {
      plans = await listPlans();
    } catch {
      plans = [];
    }
    rebuildDrawer();
  }
  refresh();

  return {
    refresh,
    setMode(m): void {
      mode = m;
      layoutsOpener.style.display = m === "floorplan" ? "" : "none";
      // Leaving floor mode hides the Layouts opener — close its drawer if open.
      if (m !== "floorplan" && open === "layouts") setOpen(null);
      else rebuildDrawer(); // model tiles re-evaluate draggability + click target
      syncSidebarInset();
    },
    setVisible(v): void {
      root.classList.toggle("show", v);
      if (!v) setOpen(null);
      syncSidebarInset();
    },
  };
}
